import React, { CSSProperties, FunctionComponent } from "react";
import { capitalize } from "lodash";
import clsx from "clsx";
import Tooltip from "./tooltip/Tooltip";
import { classificationByValues } from "../lib/enums/ThreatStatusEnum";
import ClassificationPlaceholderIcon from "./icons/ClassificationPlaceholderIcon";
import { isInfoClassification } from "./icons/utils";
import ClassificationInfoIcon from "./icons/ClassificationInfoIcon";

const UNKNOWN_RISK = 5;
const MALWARE_RISK = 0;

interface IClassificationIconProps {
  classification?: string | number;
  classificationStyle?: CSSProperties;
  riskScore?: number;
  size?: "small" | "big" | false;
  directory?: boolean;
  processingStateMessage?: string;
  style?: CSSProperties;
  className?: string;
  showTooltip?: boolean;
}

const ClassificationIcon: FunctionComponent<IClassificationIconProps> = ({
  classification = -1,
  classificationStyle = {},
  riskScore,
  size = false,
  processingStateMessage = "",
  directory = false,
  style = {},
  className,
  showTooltip = true
}) => {
  if (classification === -1) {
    return <ClassificationPlaceholderIcon style={style} />;
  }

  if (isInfoClassification(classification)) {
    return (
      <ClassificationInfoIcon
        classification={classification}
        processingStateMessage={processingStateMessage}
        style={style}
      />
    );
  }

  const hasRiskScore = riskScore || riskScore === MALWARE_RISK;

  const classificationLabel = (
    classificationByValues[classification] || (classification as string)
  ).toLowerCase();

  const sizeClassVariant = hasRiskScore ? "big" : size;

  const displayRiskScore = hasRiskScore && classificationLabel !== "unknown";

  const threatClassName = `${
    directory ? "threat--directory" : "threat"
  } threat--${classificationLabel} ${
    size || hasRiskScore ? `threat--${sizeClassVariant}` : ``
  }
    ${displayRiskScore && riskScore === UNKNOWN_RISK ? `threat--factor-5` : ``}
  `;

  const tooltipContent = `${capitalize(classificationLabel)}${
    hasRiskScore && classificationLabel !== "unknown" ? ` (${riskScore})` : ""
  }`;

  return (
    <Tooltip content={tooltipContent} disabled={!showTooltip}>
      <span
        className={clsx(
          className,
          threatClassName,
          directory && "threat--directory--adjustment"
        )}
        style={classificationStyle}
      >
        {displayRiskScore && (
          <span style={style} className="threat__risk-score">
            {riskScore}
          </span>
        )}
      </span>
    </Tooltip>
  );
};

export default ClassificationIcon;
